import NewNoteIcon from "assets/images/icons/new-note.svg";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { userSelector } from "store/auth";
import { globalsSelector } from "store/globals";
import { segmentsSelector } from "store/segments";
import { getMedicalSegmentFromSegments, isJustmedsTenant, isOpenedInIframe } from "utils/helpers";
import vars from "utils/vars";
import LocaleLink from "views/components/LocaleLink";
import { default as AppointmentModal } from "views/modals/Appointment";
import { default as AppointmentPatientModal } from "views/modals/AppointmentPatient";

const HeaderCreateNote = () => {
	const { t } = useTranslation();

	// redux global state
	const user = useSelector(userSelector);
	const globals = useSelector(globalsSelector);
	const segments = useSelector(segmentsSelector);

	// ref hook
	const dropdownRef = useRef(null);

	// state hook
	const [patientState, setPatientState] = useState({});
	const [appointmentState, setAppointmentState] = useState({});
	const [isPatientModalShownState, setIsPatientModalShownState] = useState(false);
	const [isAppointmentModalShownState, setIsAppointmentModalShownState] = useState(false);

	// constants
	const medicalSegment = useMemo(() => getMedicalSegmentFromSegments(segments), [segments]);
	const isCurrentSegmentMedicalSegment = useMemo(() => {
		const medicalSegmentId = medicalSegment?.id;
		if (!medicalSegmentId) return false;
		return medicalSegmentId === globals?.segment?.id || medicalSegmentId === user?.segment_id;
	}, [medicalSegment?.id, globals?.segment?.id, user?.segment_id]);
	const isUserHasUnlimitedNotes = Boolean(+user?.subscription?.usage?.notes?.total < 0);
	const isUserReachesHisNotesLimit = Boolean(
		!isUserHasUnlimitedNotes && +user?.subscription?.usage?.notes?.used >= +user?.subscription?.usage?.notes?.total
	);
	const isUserTypeOfficeStaff = user?.type === vars.app.usersTypes.officeStaff;
	const isDemoTenant = Boolean(user?.tenant?.is_demo);
	const showCreateNewOrder =
		isCurrentSegmentMedicalSegment && !isUserTypeOfficeStaff && !isDemoTenant;
	const CreateNoteComponentName = isUserReachesHisNotesLimit || isUserTypeOfficeStaff ? "button" : LocaleLink;
	const callToActionButtonClassNames =
		"btn btn-link text-decoration-none text-reset rounded-0 border-0 h-100 py-2 px-0 p-md-3 flex-shrink-0 d-inline-flex align-items-center white-space-nowrap";
	const createAssessmentLabel = isUserTypeOfficeStaff
		? t("actions.create_new_appointment")
		: t("actions.create_new_assessment");

	const handleAssessmentClick = () => {
		if (!isUserTypeOfficeStaff || isUserReachesHisNotesLimit) return false;
		setPatientState({});
		setIsPatientModalShownState(true);
	};

	const renderCreateAssessmentAction = (className) => (
		<CreateNoteComponentName
			to={!isUserReachesHisNotesLimit && !isUserTypeOfficeStaff ? "/notes/store" : undefined}
			type={isUserReachesHisNotesLimit || isUserTypeOfficeStaff ? "button" : undefined}
			className={className}
			data-bs-toggle={isUserReachesHisNotesLimit ? "modal" : undefined}
			data-bs-target={isUserReachesHisNotesLimit ? "#createNoteAfterReachingLimitsModal" : undefined}
			onClick={handleAssessmentClick}>
			<small>
				<strong>{createAssessmentLabel}</strong>
			</small>
		</CreateNoteComponentName>
	);

	const renderCreateOrderAction = (className) => (
		<LocaleLink to="/orders/store" className={className}>
			<small>
				<strong>{t("titles.create_new_order")}</strong>
			</small>
		</LocaleLink>
	);

	const isJustMeds = isJustmedsTenant();
	const isInIframe = isOpenedInIframe();

	if (isJustMeds && isInIframe) {
		return null;
	}

	return (
		<div className="root-header-create-note bg-yellow shadow-sm rounded-3 rounded-end-0 h-100 p-1">
			<div className="d-none d-md-flex hstack gap-1 justify-content-center flex-nowrap h-100">
				<span className="root-header-create-note-icon h-100 py-1 px-2 d-inline-flex align-items-center justify-content-center flex-shrink-0">
					<img src={NewNoteIcon} width="37" height="37" alt="" role="presentation" />
				</span>
				<div className="vr border border-body bg-transparent h-100 op-100"></div>
				<div className="root-header-create-note-cta p-1 h-100 hstack gap-0 flex-nowrap align-items-stretch">
					{renderCreateAssessmentAction(callToActionButtonClassNames)}
					{showCreateNewOrder && (
						<>
							<div className="vr border border-body bg-transparent h-100 op-100 flex-shrink-0"></div>
							{renderCreateOrderAction(callToActionButtonClassNames)}
						</>
					)}
				</div>
			</div>
			<div className="dropdown d-md-none h-100">
				<button
					className="btn btn-link text-decoration-none text-reset rounded-0 border-0 w-100 h-100 py-2 px-0 p-md-3 flex-grow-1 d-inline-flex align-items-center white-space-nowrap h-100"
					type="button"
					data-bs-toggle="dropdown"
					ref={dropdownRef}
					aria-expanded="false"
					aria-label={createAssessmentLabel}>
					<span className="root-header-create-note-icon h-100 py-1 px-2 d-inline-flex align-items-center justify-content-center flex-shrink-0">
						<img src={NewNoteIcon} width="37" height="37" alt="" role="presentation" />
					</span>
				</button>
				<ul className="dropdown-menu dropdown-menu-end w-fit-content mt-1 rounded text-capitalize shadow-sm">
					<li>{renderCreateAssessmentAction("dropdown-item text-capitalize")}</li>
					{showCreateNewOrder && <li>{renderCreateOrderAction("dropdown-item text-capitalize")}</li>}
				</ul>
			</div>
			{isUserTypeOfficeStaff && !isUserReachesHisNotesLimit && (
				<>
					<AppointmentPatientModal
						onConfirm={(patient) => {
							setPatientState(patient);
							setIsAppointmentModalShownState(true);
						}}
						isShown={isPatientModalShownState}
						setShown={setIsPatientModalShownState}
					/>
					<AppointmentModal
						appointment={appointmentState}
						patient={patientState}
						isShown={isAppointmentModalShownState}
						setShown={setIsAppointmentModalShownState}
						setPatientShown={setIsPatientModalShownState}
						onSuccessCb={() => {
							setPatientState({});
							setAppointmentState({});
						}}
					/>
				</>
			)}
		</div>
	);
};

export default HeaderCreateNote;
